import React, { useState, useEffect } from 'react';
import { Check, X, Clock, Plus, Trash2, Tag, Calendar, Percent, DollarSign } from 'lucide-react';
import { paymentsAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

const AdminPayments = () => {
  const [payments, setPayments] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [promoModalOpen, setPromoModalOpen] = useState(false);
  const [newPromo, setNewPromo] = useState({
    code: '',
    discount_percent: '',
    discount_amount: '',
    max_uses: '100',
    valid_until: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [paymentsRes, promosRes] = await Promise.all([
        paymentsAPI.getAll(),
        paymentsAPI.getPromoCodes(),
      ]);
      setPayments(paymentsRes.data);
      setPromoCodes(promosRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyPayment = async (paymentId) => {
    try {
      await paymentsAPI.verify(paymentId);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to verify payment');
    }
  };

  const handleRejectPayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to reject this payment?')) return;
    try {
      await paymentsAPI.reject(paymentId);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to reject payment');
    }
  };

  const handleCreatePromo = async () => {
    try {
      const data = {
        code: newPromo.code,
        discount_percent: parseFloat(newPromo.discount_percent) || 0,
        discount_amount: parseFloat(newPromo.discount_amount) || 0,
        max_uses: parseInt(newPromo.max_uses) || 100,
        valid_until: new Date(newPromo.valid_until).toISOString(),
        is_active: true,
      };
      await paymentsAPI.createPromoCode(data);
      setPromoModalOpen(false);
      setNewPromo({ code: '', discount_percent: '', discount_amount: '', max_uses: '100', valid_until: '' });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create promo code');
    }
  };

  const handleDeletePromo = async (promoId) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) return;
    try {
      await paymentsAPI.deletePromoCode(promoId);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete promo code');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-600',
      verified: 'bg-green-600',
      rejected: 'bg-red-600',
    };
    return <Badge className={styles[status] || 'bg-gray-600'}>{status}</Badge>;
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-white">Payments & Promo Codes</h1>

      <Tabs defaultValue="payments" className="w-full">
        <TabsList className="bg-[#1a1a2e] border border-gray-800">
          <TabsTrigger value="payments" className="data-[state=active]:bg-purple-600">
            Payments ({payments.filter(p => p.status === 'pending').length} pending)
          </TabsTrigger>
          <TabsTrigger value="promos" className="data-[state=active]:bg-purple-600">
            Promo Codes ({promoCodes.length})
          </TabsTrigger>
        </TabsList>

        {/* Payments Tab */}
        <TabsContent value="payments" className="mt-4">
          <div className="bg-[#1a1a2e] rounded-xl border border-gray-800 overflow-hidden">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : payments.length === 0 ? (
              <div className="text-center py-12">
                <Clock size={48} className="text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No payments yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-800 hover:bg-transparent">
                    <TableHead className="text-gray-400">User</TableHead>
                    <TableHead className="text-gray-400">Package</TableHead>
                    <TableHead className="text-gray-400">Amount</TableHead>
                    <TableHead className="text-gray-400">Transaction ID</TableHead>
                    <TableHead className="text-gray-400">Promo</TableHead>
                    <TableHead className="text-gray-400">Status</TableHead>
                    <TableHead className="text-gray-400">Date</TableHead>
                    <TableHead className="text-gray-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={payment.id} className="border-gray-800 hover:bg-gray-800/50">
                      <TableCell className="text-white">{payment.user_id.slice(0, 8)}...</TableCell>
                      <TableCell className="text-white">{payment.package_id.slice(0, 8)}...</TableCell>
                      <TableCell>
                        <div>
                          <p className="text-white">${payment.amount_usd.toFixed(2)}</p>
                          <p className="text-orange-400 text-xs">৳{payment.amount_bdt.toFixed(2)}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-300 font-mono text-sm">{payment.transaction_id}</TableCell>
                      <TableCell className="text-gray-400">{payment.promo_code || '-'}</TableCell>
                      <TableCell>{getStatusBadge(payment.status)}</TableCell>
                      <TableCell className="text-gray-400 text-sm">
                        {new Date(payment.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {payment.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button
                              size="icon"
                              onClick={() => handleVerifyPayment(payment.id)}
                              className="bg-green-600 hover:bg-green-700 h-8 w-8"
                            >
                              <Check size={16} />
                            </Button>
                            <Button
                              size="icon"
                              onClick={() => handleRejectPayment(payment.id)}
                              className="bg-red-600 hover:bg-red-700 h-8 w-8"
                            >
                              <X size={16} />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        {/* Promo Codes Tab */}
        <TabsContent value="promos" className="mt-4">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setPromoModalOpen(true)} className="bg-purple-600 hover:bg-purple-700">
              <Plus size={18} className="mr-2" />
              Create Promo Code
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {promoCodes.map((promo) => (
              <div
                key={promo.id}
                className={`bg-[#1a1a2e] rounded-xl p-4 border transition-all ${
                  promo.is_active && new Date(promo.valid_until) > new Date()
                    ? 'border-gray-800'
                    : 'border-red-800/50 opacity-60'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <Badge className="bg-purple-600 text-lg font-mono">{promo.code}</Badge>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleDeletePromo(promo.id)}
                    className="text-gray-400 hover:text-red-500 h-8 w-8"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-400">
                    {promo.discount_percent > 0 ? (
                      <>
                        <Percent size={14} />
                        <span>{promo.discount_percent}% off</span>
                      </>
                    ) : (
                      <>
                        <DollarSign size={14} />
                        <span>${promo.discount_amount.toFixed(2)} off</span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-gray-400">
                    <Tag size={14} />
                    <span>{promo.current_uses} / {promo.max_uses} uses</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400">
                    <Calendar size={14} />
                    <span>Expires: {new Date(promo.valid_until).toLocaleDateString()}</span>
                  </div>
                </div>

                <div className="mt-3">
                  {new Date(promo.valid_until) < new Date() ? (
                    <Badge className="bg-red-600">Expired</Badge>
                  ) : promo.current_uses >= promo.max_uses ? (
                    <Badge className="bg-orange-600">Max Uses Reached</Badge>
                  ) : (
                    <Badge className="bg-green-600">Active</Badge>
                  )}
                </div>
              </div>
            ))}

            {promoCodes.length === 0 && (
              <div className="col-span-full text-center py-12">
                <Tag size={48} className="text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No promo codes yet</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Promo Modal */}
      <Dialog open={promoModalOpen} onOpenChange={setPromoModalOpen}>
        <DialogContent className="bg-[#1a1a2e] border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Tag size={20} />
              Create Promo Code
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-400">Promo Code</Label>
              <Input
                value={newPromo.code}
                onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
                placeholder="e.g., SAVE20"
                className="bg-[#12121a] border-gray-700 text-white uppercase"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-400">Discount %</Label>
                <Input
                  type="number"
                  value={newPromo.discount_percent}
                  onChange={(e) => setNewPromo({ ...newPromo, discount_percent: e.target.value, discount_amount: '' })}
                  placeholder="20"
                  className="bg-[#12121a] border-gray-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">Or Fixed Amount ($)</Label>
                <Input
                  type="number"
                  value={newPromo.discount_amount}
                  onChange={(e) => setNewPromo({ ...newPromo, discount_amount: e.target.value, discount_percent: '' })}
                  placeholder="5.00"
                  className="bg-[#12121a] border-gray-700 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-400">Max Uses</Label>
                <Input
                  type="number"
                  value={newPromo.max_uses}
                  onChange={(e) => setNewPromo({ ...newPromo, max_uses: e.target.value })}
                  placeholder="100"
                  className="bg-[#12121a] border-gray-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">Valid Until</Label>
                <Input
                  type="date"
                  value={newPromo.valid_until}
                  onChange={(e) => setNewPromo({ ...newPromo, valid_until: e.target.value })}
                  className="bg-[#12121a] border-gray-700 text-white"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPromoModalOpen(false)} className="border-gray-700 text-gray-400">
              Cancel
            </Button>
            <Button
              onClick={handleCreatePromo}
              disabled={!newPromo.code || !newPromo.valid_until || (!newPromo.discount_percent && !newPromo.discount_amount)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPayments;
